import React, { useCallback, useEffect, useState } from 'react';
import { contractAdjustmentsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import { fmtBRL } from '../utils/adjustments';
import { rotuloDoSubtipo } from '../utils/aditivosDoContrato';
import PreviaDoReajuste from './PreviaDoReajuste';
import { campo } from '../design';

/**
 * TERMOS ADITIVOS ASSINADOS QUE AINDA NÃO VIRARAM NADA.
 *
 * ⛔ POR QUE ISTO EXISTE. O aditivo assinado grava o fato, e só. Aplicar mexe em
 * MRR, reajusta item a item, reajusta tarifa de consumo e pode emitir fatura
 * complementar — e a data que manda está numa cláusula que uma PESSOA leu. No
 * aditivo do Sicoob a 3.3 fixa novembro/2026, enquanto "primeiro mês cheio após
 * a vigência" daria julho: quatro competências que o instrumento não manda
 * cobrar. Por isso o backend tirou a aplicação do webhook e a fez ação
 * explícita, com prévia (#238). Esta é a porta dela.
 *
 * Sem esta lista, as duas rotas existiam e ninguém tinha botão: o aditivo
 * assinado ficaria parado, como os de 2021 e 2023 do Sicoob, aplicados à mão e
 * invisíveis ao controle.
 *
 * ⛔ `effectsFrom` NÃO TEM PADRÃO, e a tela não inventa um. Preencher com um
 * palpite plausível é o jeito mais rápido de alguém aceitar sem ler a cláusula
 * — que é exatamente o erro de quatro meses que o backend recusou cometer.
 */

interface Pendente {
  digitalContractId: number;
  contratoId: number;
  cliente: string;
  rotulo: string;
  subtipos: string[];
  assinadoEm: string | null;
  vigenciaPendente: boolean;
  reajustePendente: boolean;
  vigenciaFim: string | null;
  contratoVigenciaFim: string | null;
  mrrDe: number;
  mrrPara: number | null;
}

interface Props {
  canEdit?: boolean;
  /** Chamado depois de aplicar, para a tela de reajustes recarregar. */
  onAplicado?: () => void;
}

export default function AditivosPendentesPanel({ canEdit = true, onAplicado }: Props) {
  const [lista, setLista] = useState<Pendente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<Pendente | null>(null);
  const [effectsFrom, setEffectsFrom] = useState('');
  const [applyAt, setApplyAt] = useState('');
  const [percentual, setPercentual] = useState('');
  const [previa, setPrevia] = useState<any>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null);
    try {
      const r = await contractAdjustmentsService.aditivosPendentes();
      setLista(Array.isArray(r) ? r : []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os aditivos pendentes');
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = (p: Pendente) => {
    setAberto(p);
    setEffectsFrom('');   // sem padrão, de propósito
    setApplyAt('');
    setPercentual('');
    setPrevia(null);
    setErro(null);
  };

  const corpo = (dryRun: boolean) => ({
    effectsFrom,
    applyAt: applyAt || undefined,
    percentual: percentual === '' ? undefined : Number(percentual),
    dryRun,
  });

  const verPrevia = async () => {
    if (!aberto || !effectsFrom) return;
    setOcupado('previa'); setErro(null);
    try {
      setPrevia(await contractAdjustmentsService.aplicarAditivo(aberto.digitalContractId, corpo(true)));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível calcular a prévia');
      setPrevia(null);
    } finally { setOcupado(null); }
  };

  const aplicar = async () => {
    if (!aberto || !previa) return;
    const oQueMuda = [
      `Vigência do contrato → ${aberto.vigenciaFim ? fmtDate(aberto.vigenciaFim) : 'sem mudança'}`,
      `Mensal ${fmtBRL(previa.contract?.mrrBefore)} → ${fmtBRL(previa.contract?.mrrAfter)}`,
      previa.retro?.months?.length
        ? `Retroativo de ${previa.retro.months.length} competência(s): ${fmtBRL(previa.retro.total)}`
        : 'Sem retroativo',
    ].join('\n');
    if (!window.confirm(`Aplicar o ${aberto.rotulo} ao contrato #${aberto.contratoId}?\n\n${oQueMuda}\n\nIsto grava. A fatura complementar, se houver, é emitida.`)) return;
    setOcupado('aplicar'); setErro(null);
    try {
      await contractAdjustmentsService.aplicarAditivo(aberto.digitalContractId, corpo(false));
      setAberto(null);
      await carregar();
      onAplicado?.();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível aplicar o aditivo');
    } finally { setOcupado(null); }
  };

  if (carregando) return null;
  if (!lista.length && !erro) return null;

  return (
    <div className='mt-3 rounded-lg border border-blue-500/25 bg-blue-500/5 p-3'>
      <p className='text-xs text-blue-300/90 font-medium'>
        Termos aditivos assinados esperando aplicação
        <span className='text-blue-300/50 font-normal'> · assinar registra o fato; aplicar é decisão de quem leu a cláusula</span>
      </p>

      {erro && <p className='mt-2 text-xs text-red-400'>{erro}</p>}

      <div className='mt-2 flex flex-col gap-1.5'>
        {lista.map((p) => (
          <button
            key={p.digitalContractId}
            type='button'
            onClick={() => abrir(p)}
            className='w-full flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md bg-slate-800/60 border border-slate-700 hover:border-blue-500/60 text-left'
            title='Conferir a prévia e aplicar'
          >
            <span className='min-w-0'>
              <span className='block text-sm text-white truncate'>{p.cliente}</span>
              <span className='block text-xs text-slate-500'>
                #{p.contratoId} · {p.rotulo}
                {p.assinadoEm && <> · assinado {fmtDate(p.assinadoEm)}</>}
              </span>
            </span>
            <span className='text-right shrink-0 flex items-center gap-1.5'>
              {/* Separados porque são dois trabalhos, e um pode já ter sido feito. */}
              {p.vigenciaPendente && (
                <span className='text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300'>vigência</span>
              )}
              {p.reajustePendente && (
                <span className='text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-300'>reajuste</span>
              )}
              {p.mrrPara != null && p.mrrPara !== p.mrrDe && (
                <span className='text-xs text-slate-300 whitespace-nowrap'>{fmtBRL(p.mrrDe)} → {fmtBRL(p.mrrPara)}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      {aberto && (
        <div className='mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-3 space-y-3'>
          <div className='flex items-start justify-between gap-3'>
            <div>
              <p className='text-sm font-semibold text-white'>{aberto.rotulo} · {aberto.cliente}</p>
              <p className='text-xs text-slate-500'>
                Contrato #{aberto.contratoId}
                {aberto.subtipos.length > 0 && <> · {aberto.subtipos.map(rotuloDoSubtipo).join(', ')}</>}
              </p>
              {aberto.vigenciaFim && (
                <p className='text-xs text-slate-500 mt-0.5'>
                  Vigência do contrato: {aberto.contratoVigenciaFim ? fmtDate(aberto.contratoVigenciaFim) : 'sem data'} → {fmtDate(aberto.vigenciaFim)}
                </p>
              )}
            </div>
            <button type='button' onClick={() => setAberto(null)} className='text-slate-400 hover:text-white text-sm px-2'>fechar</button>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <div className='md:col-span-1'>
              <label className='block text-xs text-slate-400 mb-1'>Primeira competência atingida *</label>
              <input type='date' value={effectsFrom} onChange={(e) => { setEffectsFrom(e.target.value); setPrevia(null); }}
                className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
            </div>
            <div>
              <label className='block text-xs text-slate-400 mb-1'>Aplicar em (opcional)</label>
              <input type='date' value={applyAt} onChange={(e) => { setApplyAt(e.target.value); setPrevia(null); }}
                className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
              <p className='text-[10px] text-slate-500 mt-1'>Vazio: aplica agora, ou agenda para a competência se ela for futura.</p>
            </div>
            <div>
              <label className='block text-xs text-slate-400 mb-1'>Percentual (opcional)</label>
              <input type='number' step='0.01' min='0' placeholder='4.64' value={percentual}
                onChange={(e) => { setPercentual(e.target.value); setPrevia(null); }}
                className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`} />
              <p className='text-[10px] text-slate-500 mt-1'>Vazio: derivado dos valores do instrumento.</p>
            </div>
          </div>

          {/* ⛔ O campo nasce VAZIO de propósito. A data está numa cláusula que
              alguém precisa ler: no Sicoob a 3.3 fixa novembro/2026, e a regra
              automática daria julho — quatro competências que o instrumento não
              manda cobrar. Um padrão plausível aqui seria aceito sem leitura. */}
          <p className='text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2'>
            <b>A competência sai da cláusula, não de uma regra.</b> O campo fica vazio de propósito: no aditivo
            do Sicoob a cláusula 3.3 fixa novembro/2026, enquanto "primeiro mês cheio após a vigência" daria
            julho — quatro competências que o instrumento não manda cobrar.
          </p>

          {previa && <PreviaDoReajuste preview={previa} />}

          <div className='flex justify-end gap-2'>
            <button type='button' onClick={verPrevia} disabled={!effectsFrom || !!ocupado}
              className='px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50'
              title={!effectsFrom ? 'Informe a primeira competência atingida' : ''}>
              {ocupado === 'previa' ? 'Calculando…' : 'Prévia'}
            </button>
            {canEdit && (
              <button type='button' onClick={aplicar} disabled={!previa || !!ocupado}
                className='px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm disabled:opacity-50'
                title={!previa ? 'Veja a prévia antes de aplicar' : ''}>
                {ocupado === 'aplicar' ? 'Aplicando…' : 'Aplicar'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
